import { useCallback, useEffect, useState } from 'react';

import { CLERK_RESOURCE_STATUS, CLERK_STRATEGY } from '@/features/auth/shared/consts/clerk';
import type { AuthError } from '@/features/auth/shared/types/auth';
import { mapClerkError } from '@/features/auth/shared/utils/clerkErrorMapper';
import { useSignUp } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

interface AcceptInvitationFormValues {
  firstName: string;
  lastName: string;
  password: string;
}

export const useAcceptInvitation = () => {
  const { signUp, setActive, isLoaded } = useSignUp();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState<'loading' | 'form' | 'error' | 'migrated'>('loading');
  const [error, setError] = useState<AuthError | null>(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState<string | null>(null);

  const ticket = searchParams.get('__clerk_ticket');
  const clerkStatus = searchParams.get('__clerk_status');

  const acceptInvitation = useCallback(async () => {
    if (!isLoaded) return;

    if (!ticket) {
      setStep('error');
      return;
    }

    if (clerkStatus === 'sign_in') {
      setStep('migrated');
      return;
    }

    try {
      const result = await signUp.create({
        strategy: CLERK_STRATEGY.TICKET,
        ticket
      });
      setEmail(result.emailAddress ?? null);
      setStep('form');
    } catch (err) {
      setError(mapClerkError(err, t));
      setStep('error');
    }
  }, [isLoaded, ticket, clerkStatus, signUp, t]);

  useEffect(() => {
    void acceptInvitation();
  }, [acceptInvitation]);

  const handleSubmit = useCallback(
    async (data: AcceptInvitationFormValues) => {
      if (!signUp) return false;

      setLoading(true);
      setError(null);

      try {
        // Update user profile and set password
        await signUp.update({
          firstName: data.firstName,
          lastName: data.lastName,
          password: data.password
        });

        // Complete the sign-up
        if (signUp.status === CLERK_RESOURCE_STATUS.COMPLETE) {
          await setActive({ session: signUp.createdSessionId });
          toast.success(t('success.auth.acceptInvitation'));
          void navigate('/');
          return true;
        }

        setError({ type: 'general', message: t('error.acceptInvitation.unexpectedStatus') });
        return false;
      } catch (err) {
        setError(mapClerkError(err, t));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [signUp, setActive, navigate, t]
  );

  return {
    step,
    error,
    loading,
    isLoaded,
    email,
    hasTicket: !!ticket,
    handleSubmit
  };
};
